import React from "react";
import { ArrowRight } from "lucide-react";

type TeamsSectionProps = {
  title: string;
  description: string;
  uses: string[];
  image: string;
  imageAlt: string;
  /** Navy fade over the bottom of the photo, where the design shows one */
  imageGradient?: boolean;
};

/** Dark "For Teams" block with Speak to Our Team / Request a Quote. */
export default function TeamsSection({ title, description, uses, image, imageAlt, imageGradient }: TeamsSectionProps) {
  return (
    <section className="relative overflow-hidden bg-[#012d6e] py-20">
      <div className="pointer-events-none absolute right-0 top-0 h-[400px] w-[500px] rounded-full bg-[#017bef]/15 blur-[64px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[300px] w-[400px] rounded-full bg-[#0062c0]/10 blur-[64px]" />

      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <span className="inline-block rounded-full border-[1px] border-solid border-white/15 bg-white/10 px-[16px] py-1.5 text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-white/70">
            For Teams
          </span>
          <h2 className="pt-[12px] text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-white sm:text-[48px] sm:leading-[48px]">
            {title}
          </h2>
          <p className="pt-[20px] text-[18px] leading-[29.25px] text-white/60">{description}</p>
          <ul className="grid grid-cols-2 gap-[12px] pt-8">
            {uses.map(use => (
              <li key={use} className="flex items-center gap-2 text-[14px] leading-5 text-white/70">
                <span className="size-1.5 shrink-0 rounded-full bg-[#60a5fa]" />
                {use}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-[16px] pt-8">
            <a
              href="mailto:sales@spacia.africa"
              className="inline-flex items-center gap-2 rounded-full bg-[#017bef] px-7 py-3.5 text-[14px] font-bold leading-5 text-white no-underline transition-colors hover:bg-[#0062c0] hover:text-white"
            >
              Speak to Our Team
              <ArrowRight className="size-[15px]" />
            </a>
            <button
              type="button"
              onClick={() => (window.location.href = "mailto:sales@spacia.africa")}
              className="rounded-full border-[1px] border-solid border-white/20 px-7 py-3.5 text-[14px] font-semibold leading-5 text-white transition-colors hover:bg-white/10"
            >
              Request a Quote
            </button>
          </div>
        </div>

        <div className="relative h-[360px] overflow-hidden rounded-3xl sm:h-[492px]">
          <img src={image} alt={imageAlt} className="absolute inset-0 size-full object-cover" />
          {imageGradient && (
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(1,45,110,0.5)] to-[rgba(1,45,110,0)]" />
          )}
        </div>
      </div>
    </section>
  );
}
